import React, { useState } from 'react';
import { Share2, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Compact share button that turns the member's earned savings into a
 * referral nudge. Uses the native Web Share API when present, falls back
 * to clipboard copy on desktop. Renders nothing if `savings <= 0` and
 * `hideWhenEmpty` is set (caller's choice).
 */
export function ShareSavingsButton({
  savings = 0,
  claims = 0,
  referralCode,
  className = '',
  hideWhenEmpty = false,
  testid = 'share-savings-btn',
}) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  if (hideWhenEmpty && (!savings || savings <= 0)) return null;

  const link = `${window.location.origin}/register${referralCode ? `?ref=${referralCode}` : ''}`;
  const formattedSavings = `PHP ${Number(savings || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const text = savings > 0
    ? `I saved ${formattedSavings} across ${claims} purchases with All Global Access — join me and start saving on every merchant in PH.`
    : `I'm an All Global Access member — discounted services + a real stock certificate when you subscribe. Join me!`;

  const onShare = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setBusy(true);
    try {
      if (navigator.share) {
        await navigator.share({ title: 'All Global Access', text, url: link });
        toast.success('Thanks for sharing!');
      } else {
        await navigator.clipboard.writeText(`${text} ${link}`);
        setCopied(true);
        toast.success('Share text copied — paste it anywhere!');
        setTimeout(() => setCopied(false), 2200);
      }
    } catch (err) {
      // User cancelled or share failed — fall through to clipboard
      if (err?.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(`${text} ${link}`);
          setCopied(true);
          toast.success('Share text copied!');
          setTimeout(() => setCopied(false), 2200);
        } catch {
          toast.error('Could not open share sheet');
        }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onShare}
      disabled={busy}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-lg border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-3 py-1.5 text-[12px] text-[#00E5FF] hover:bg-[#00E5FF]/20 hover:border-[#00E5FF]/60 transition-colors disabled:opacity-60 ${className}`}
      title="Share your AGA savings"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
      <span className="font-medium">{copied ? 'Copied' : 'Share'}</span>
    </button>
  );
}

export default ShareSavingsButton;
